<template>
<v-card id="account-setting-card">

    <v-tabs v-model="tab" show-arrows>
      <v-tab v-for="t in tabs" :key="t.icon">
        <v-icon size="20" class="me-3">{{ t.icon }}</v-icon>
        <span>{{ t.title }}</span>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">

      <v-tab-item>
        <account-settings-info></account-settings-info>
      </v-tab-item>

      <v-tab-item>
        <account-settings-account></account-settings-account>
      </v-tab-item>

      <v-tab-item>
        <account-settings-security></account-settings-security>
      </v-tab-item>

      <v-tab-item>
        <v-card flat class="pa-3 mt-2">

          <!-- CAMPAIGNS -->
          <v-card-text>
            <v-card-title class="mt-0 pt-0">
              <span class="me-3">({{ clientCampaigns.length }}) Campaigns</span>
              <v-spacer></v-spacer>
              <v-text-field v-model="searchCampaigns" append-icon="mdi-magnify" label="Search" single-line hide-details />
            </v-card-title>
            <h3 class="ml-3">Total Cash Saved: {{ savings_total || 0 }} F CFA</h3>
            <v-data-table :headers="headers" :items="clientCampaigns" item-key="id" class="table-rounded" :items-per-page="10" disable-sort :search="searchCampaigns">
              <template #[`item.name`]="{item}">
                <router-link :to="'/savings/clients/campaign/'+item.id">
                  <div class="d-flex flex-column" style="cursor:pointer">
                    <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.name || '—' }}</span>
                    <small v-if="item.target_date && item.target_amount">Type: <b>Date & Amount</b> at {{ item.percentage_completed || '0%' }} Completion</small>
                    <small v-else-if="item.target_amount">Type: <b>Amount</b> at {{ item.percentage_completed || '0%' }} Completion</small>
                    <small v-else-if="item.target_date">Type: <b>Date</b> at {{ item.percentage_completed || '0%' }} Completion</small>
                    <small v-else class="text--secondary">Type: <b>Undefined</b></small>
                  </div>
                </router-link>
              </template>
              <template #[`item.saved_amount`]="{item}">
                <span v-if="item.saved_amount != null">{{ item.saved_amount }} F CFA</span>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.status`]="{item}">
                <v-chip v-if="item.status" small :color="statusColor[item.status] || 'grey'" dark class="font-weight-medium">{{ item.status }}</v-chip>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.get_date_str`]="{item}">
                <span v-if="item.get_date_str">{{ item.get_date_str }}</span>
                <span v-else class="text--secondary">—</span>
              </template>
            </v-data-table>
          </v-card-text>

          <!-- LOANS -->
          <v-card-text>
            <v-card-title class="mt-0 pt-0">
              <span class="me-3">({{ clientLoans.length }}) Loans</span>
              <v-spacer></v-spacer>
              <v-text-field v-model="searchLoans" append-icon="mdi-magnify" label="Search" single-line hide-details />
            </v-card-title>
            <h3 class="ml-3">Total Cash Owing: {{ owing_total || 0 }} F CFA</h3>
            <v-data-table :headers="headers" :items="clientLoans" item-key="id" class="table-rounded" :items-per-page="10" disable-sort :search="searchLoans">
              <template #[`item.name`]="{item}">
                <router-link :to="'/savings/clients/loan/'+item.id">
                  <div class="d-flex flex-column" style="cursor:pointer">
                    <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.name || '—' }}</span>
                    <small v-if="item.target_date && item.target_amount">Type: <b>Date & Amount</b> at {{ item.percentage_completed || '0%' }} Completion</small>
                    <small v-else-if="item.target_amount">Type: <b>Amount</b> at {{ item.percentage_completed || '0%' }} Completion</small>
                    <small v-else-if="item.target_date">Type: <b>Date</b> at {{ item.percentage_completed || '0%' }} Completion</small>
                    <small v-else class="text--secondary">Type: <b>Undefined</b></small>
                  </div>
                </router-link>
              </template>
              <template #[`item.saved_amount`]="{item}">
                <span v-if="item.saved_amount != null">
                  {{ item.saved_amount }} F CFA
                  <span class="text--secondary caption">{{ item.is_legacy ? '(Saved)' : '(Paid)' }}</span>
                </span>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.status`]="{item}">
                <v-chip v-if="item.status" small :color="statusColor[item.status] || 'grey'" dark class="font-weight-medium">{{ item.status }}</v-chip>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.get_date_str`]="{item}">
                <span v-if="item.get_date_str">{{ item.get_date_str }}</span>
                <span v-else class="text--secondary">—</span>
              </template>
            </v-data-table>
          </v-card-text>

          <!-- WALLET -->
          <v-card-text>
            <v-card-title class="mt-0 pt-0">
              <v-icon class="me-2" color="indigo">mdi-wallet</v-icon>
              <span>Wallet</span>
              <v-spacer></v-spacer>
            </v-card-title>
            <h3 class="ml-3">Current Balance: {{ walletBalance || 0 }} F CFA</h3>
            <v-data-table :headers="walletHeaders" :items="walletTransactions" item-key="id" class="table-rounded mt-3" :items-per-page="5" disable-sort>
              <template #[`item.transaction_type`]="{item}">
                <div v-if="item.transaction_type">
                  <div :style="{ display:'inline-flex', alignItems:'center', padding:'3px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'600', color:'#fff', background: walletTypeHexColor(item.transaction_type), whiteSpace:'nowrap' }">
                    <v-icon small style="color:#fff;margin-right:4px">{{ walletTypeIcon(item.transaction_type) }}</v-icon>
                    {{ item.transaction_type_display || '—' }}
                  </div>
                  <div v-if="item.source" class="mt-1">
                    <v-chip x-small outlined color="blue-grey">
                      <v-icon x-small left>mdi-arrow-collapse-right</v-icon>
                      from {{ item.source_display || item.source }}
                    </v-chip>
                  </div>
                  <div v-if="item.destination" class="mt-1">
                    <v-chip x-small outlined color="blue-grey">
                      <v-icon x-small left>mdi-arrow-expand-right</v-icon>
                      to {{ item.destination_display || item.destination }}
                    </v-chip>
                  </div>
                </div>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.amount`]="{item}">
                <span v-if="item.amount != null" class="font-weight-bold" :style="{ color: item.is_credit ? '#00897B' : '#E53935' }">
                  {{ item.is_credit ? '+' : '-' }}{{ item.amount }} F CFA
                </span>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.status`]="{item}">
                <div v-if="item.status" :style="{ display:'inline-flex', alignItems:'center', padding:'3px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'600', color:'#fff', background: walletStatusHexColor(item.status), whiteSpace:'nowrap' }">
                  {{ item.status }}
                </div>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.description`]="{item}">
                <span v-if="item.description" class="text-truncate" style="max-width:220px;display:block">{{ item.description }}</span>
                <span v-else class="text--secondary">—</span>
              </template>
              <template #[`item.created_at`]="{item}">
                <span v-if="item.created_at">{{ formatDate(item.created_at) }}</span>
                <span v-else class="text--secondary">—</span>
              </template>
            </v-data-table>
          </v-card-text>

          <!-- TOP-UP REQUESTS -->
          <!-- ⚠️ Le champ 'type' n'existe pas dans l'API → on utilise 'withdrawal' comme colonne -->
          <v-card-text>
            <v-card-title class="mt-0 pt-0">
              <v-icon class="me-2" color="primary">mdi-cellphone-arrow-down</v-icon>
              <span class="me-3">({{ filteredSaveTransactions.length }}) Top-Up Requests</span>
              <v-spacer></v-spacer>
              <v-text-field v-model="searchSaveTransactions" append-icon="mdi-magnify" label="Search" single-line hide-details />
            </v-card-title>
            <v-data-table
              :headers="saveTransactionHeaders"
              :items="filteredSaveTransactions"
              item-key="id"
              class="table-rounded mt-3"
              :items-per-page="5"
              disable-sort
              :search="searchSaveTransactions"
            >
            <template #[`item.withdrawal`]="{item}">
              <v-chip small dark :color="item.withdrawal === true ? 'orange darken-1' : 'teal darken-1'">
                <v-icon small left>{{ item.withdrawal === true ? 'mdi-arrow-up-bold-circle-outline' : 'mdi-arrow-down-bold-circle-outline' }}</v-icon>
                {{ item.withdrawal === true ? 'Withdrawal' : 'Deposit' }}
              </v-chip>
            </template>

            <template #[`item.confirmed`]="{item}">
              <v-chip x-small dark :color="item.cancelled ? 'red darken-1' : item.confirmed ? 'green darken-1' : 'amber darken-1'">
                <v-icon x-small left>{{ item.cancelled ? 'mdi-close-circle-outline' : item.confirmed ? 'mdi-check-circle-outline' : 'mdi-clock-outline' }}</v-icon>
                {{ item.cancelled ? 'Cancelled' : item.confirmed ? 'Confirmed' : 'Pending' }}
              </v-chip>
            </template>

              <template #[`item.campaign`]="{item}">
                <div v-if="item.campaign && item.campaign.id">
                  <router-link :to="'/savings/clients/campaign/'+item.campaign.id">
                    <span class="font-weight-medium primary--text">{{ item.campaign.name || '—' }}</span>
                  </router-link>
                  <div class="mt-1">
                    <div :style="{ display:'inline-flex', alignItems:'center', padding:'2px 6px', borderRadius:'10px', fontSize:'10px', fontWeight:'600', color:'#fff', background: item.campaign.principal ? '#6A1B9A' : '#546E7A', whiteSpace:'nowrap' }">
                      {{ item.campaign.principal ? 'Principal' : 'Tier' }}
                    </div>
                  </div>
                </div>
                <span v-else class="text--secondary">—</span>
              </template>

              <template #[`item.transaction_operator`]="{item}">
                <div v-if="item.transaction_operator && item.transaction_operator !== '-'" class="d-flex align-center">
                  <v-icon small class="me-1" color="grey">mdi-sim</v-icon>
                  <span class="font-weight-medium">{{ item.transaction_operator }}</span>
                </div>
                <span v-else class="text--secondary">—</span>
              </template>

              <template #[`item.amount`]="{item}">
                <span v-if="item.amount" class="font-weight-bold" :style="{ color: item.withdrawal ? '#FB8C00' : '#00897B' }">
                  {{ item.withdrawal ? '-' : '+' }}{{ item.amount }} F CFA
                </span>
                <span v-else class="text--secondary">—</span>
              </template>

              <template #[`item.phone`]="{item}">
                <div v-if="item.phone && item.phone !== '-'" class="d-flex align-center">
                  <v-icon small class="me-1" color="grey">mdi-phone</v-icon>
                  {{ item.phone }}
                </div>
                <span v-else class="text--secondary">—</span>
              </template>

              <!-- ✅ confirmed/cancelled sont de vrais champs API -->
              <template #[`item.confirmed`]="{item}">
                <div :style="{ display:'inline-flex', alignItems:'center', padding:'3px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'600', color:'#fff', background: item.cancelled ? '#E53935' : item.confirmed ? '#43A047' : '#FFB300', whiteSpace:'nowrap' }">
                  <v-icon small style="color:#fff;margin-right:4px">{{ item.cancelled ? 'mdi-close-circle-outline' : item.confirmed ? 'mdi-check-circle-outline' : 'mdi-clock-outline' }}</v-icon>
                  {{ item.cancelled ? 'Cancelled' : item.confirmed ? 'Confirmed' : 'Pending' }}
                </div>
              </template>

              <template #[`item.time_created`]="{item}">
                <div v-if="item.time_created" class="d-flex align-center">
                  <v-icon small class="me-1" color="grey">mdi-calendar-clock</v-icon>
                  {{ item.time_created }}
                </div>
                <span v-else class="text--secondary">—</span>
              </template>
            </v-data-table>
          </v-card-text>

          <!-- PRINCIPAL CAMPAIGN -->
          <v-card-text v-if="principalCampaign">
            <v-card-title class="mt-0 pt-0">
              <v-icon class="me-2" color="deep-purple">mdi-bank</v-icon>
              <span>Principal Campaign</span>
              <v-spacer></v-spacer>
              <v-btn color="deep-purple" dark small @click="dialogPrincipal = true">
                <v-icon left small>mdi-calculator</v-icon>
                View Balance Breakdown
              </v-btn>
            </v-card-title>
            <h3 class="ml-3">
              {{ principalCampaign.name || 'Principal' }} —
              Saved Amount: <span style="color:#6A1B9A;font-weight:bold">{{ principalCampaign.saved_amount || 0 }} F CFA</span>
            </h3>
          </v-card-text>

          <!-- DIALOGUE BREAKDOWN PRINCIPAL -->
          <v-dialog v-model="dialogPrincipal" max-width="1050" scrollable>
            <v-card v-if="principalCampaign">
              <v-card-title class="deep-purple darken-2 white--text py-3">
                <v-icon dark class="me-2">mdi-bank</v-icon>
                {{ principalCampaign.name || 'Principal Campaign' }} — Balance Breakdown
                <v-spacer></v-spacer>
                <v-btn icon dark @click="dialogPrincipal = false"><v-icon>mdi-close</v-icon></v-btn>
              </v-card-title>

              <v-card-text class="pt-4 pb-0">
                <v-row dense class="mb-4">
                  <v-col cols="6" sm="3">
                    <v-card outlined class="text-center pa-3 rounded-lg">
                      <div class="caption text--secondary mb-1">System Value</div>
                      <div class="font-weight-bold title" style="color:#6A1B9A">{{ principalCampaign.saved_amount || 0 }} F CFA</div>
                      <div class="caption text--secondary">saved_amount</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <v-card outlined class="text-center pa-3 rounded-lg">
                      <div class="caption text--secondary mb-1">Total Deposited</div>
                      <div class="font-weight-bold title" style="color:#00897B">+{{ principalSummary.totalDeposits }} F CFA</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <v-card outlined class="text-center pa-3 rounded-lg">
                      <div class="caption text--secondary mb-1">Total Withdrawn</div>
                      <div class="font-weight-bold title" style="color:#FB8C00">−{{ principalSummary.totalWithdrawals }} F CFA</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <v-card color="deep-purple darken-2" dark class="text-center pa-3 rounded-lg">
                      <div class="caption mb-1">Calculated Balance</div>
                      <div class="font-weight-bold title">{{ principalSummary.balance }} F CFA</div>
                    </v-card>
                  </v-col>
                </v-row>

                <v-alert v-if="principalSummary.pendingCount > 0" type="warning" dense text class="mb-4 caption">
                  <strong>{{ principalSummary.pendingCount }} pending request(s)</strong>
                  totaling <strong>{{ principalSummary.totalPending }} F CFA</strong>
                  awaiting validation — <u>not counted</u> in the balance above.
                </v-alert>

                <v-tabs v-model="principalTab" color="deep-purple" class="mb-3">
                  <v-tab>
                    <v-icon small left>mdi-format-list-bulleted</v-icon>
                    All Transactions ({{ principalCampaign.transactions.length }})
                  </v-tab>
                  <v-tab>
                    <v-icon small left>mdi-check-circle-outline</v-icon>
                    Completed Campaigns ({{ principalCampaign.completed_contributions.length }})
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="principalTab">
                  <v-tab-item>
                    <v-simple-table dense class="mb-2">
                      <template #default>
                        <thead>
                          <tr class="deep-purple lighten-5">
                            <th style="width:36px">#</th>
                            <th style="min-width:140px">Type</th>
                            <th style="min-width:170px">Origin / Destination</th>
                            <th style="min-width:150px">Details</th>
                            <th style="min-width:150px">Date</th>
                            <th class="text-right" style="min-width:120px">Amount</th>
                            <th class="text-right" style="min-width:120px">Running Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(tx, index) in principalTransactionsWithRunning"
                            :key="tx.id || index"
                            :style="tx.pending ? 'background:#FFF8E1' : ''"
                          >
                            <td class="caption text--secondary">{{ index + 1 }}</td>

                            <td>
<v-chip v-if="tx.pending" x-small dark color="amber darken-1">
  <v-icon x-small left>mdi-clock-outline</v-icon>Pending
</v-chip>
<v-chip v-else-if="tx.withdrawal" x-small dark color="orange darken-1">
  <v-icon x-small left>mdi-arrow-up</v-icon>Withdrawal
</v-chip>
<v-chip v-else x-small dark color="teal darken-1">
  <v-icon x-small left>mdi-arrow-down</v-icon>Deposit
</v-chip>

                            <td>
                              <div v-if="tx.source === 'completed_campaign' && tx.source_campaign">
                                <router-link :to="'/savings/clients/campaign/'+tx.source_campaign.id" @click.native="dialogPrincipal = false">
                                  <span class="font-weight-medium primary--text caption">{{ tx.source_campaign.name || '—' }}</span>
                                </router-link>
                                <div class="caption text--secondary">
                                  Saved: {{ tx.source_campaign.saved_amount || 0 }} F CFA ·
                                  <v-chip x-small :color="statusColor[tx.source_campaign.status] || 'grey'" dark>{{ tx.source_campaign.status || '—' }}</v-chip>
                                </div>
                              </div>
                              <div v-else-if="tx.source === 'wallet_transaction'">
                                <div class="d-flex align-center">
                                  <v-icon small color="indigo" class="me-1">mdi-wallet</v-icon>
                                  <span class="caption font-weight-medium indigo--text">
                                    {{ tx.withdrawal ? 'Transferred to Wallet' : 'Received from Wallet' }}
                                  </span>
                                </div>
                                <div v-if="tx.related_user" class="caption text--secondary"><v-icon x-small>mdi-account</v-icon> {{ tx.related_user }}</div>
                                <div v-if="tx.wallet_ref && tx.wallet_ref !== '-'" class="caption text--secondary">Ref: {{ tx.wallet_ref }}</div>
                                <div v-if="tx.wallet_type" class="mt-1">
                                  <v-chip x-small outlined color="indigo">{{ walletTypeLabel(tx.wallet_type) }}</v-chip>
                                </div>
                              </div>
                              <div v-else-if="tx.source === 'campaign_transaction'">
                                <div class="d-flex align-center">
                                  <v-icon small color="blue-grey" class="me-1">mdi-account-tie</v-icon>
                                  <span class="caption font-weight-medium blue-grey--text">
                                    {{ tx.withdrawal ? 'Admin Withdrawal' : 'Admin Deposit' }}
                                  </span>
                                </div>
                                <div v-if="tx.top_up_admin && tx.top_up_admin !== '-'" class="caption text--secondary">By: {{ tx.top_up_admin }}</div>
                              </div>
                              <div v-else>
                                <div class="d-flex align-center">
                                  <v-icon small color="blue" class="me-1">mdi-cellphone</v-icon>
                                  <span class="caption blue--text">Mobile Request</span>
                                </div>
                                <span v-if="tx.pending" class="caption text--secondary">(awaiting validation)</span>
                              </div>
                            </td>

                            <td>
                              <div v-if="tx.means_of_payment && tx.means_of_payment !== '-' && tx.means_of_payment !== 'Campaign Completion'" class="d-flex align-center">
                                <v-icon x-small class="me-1" color="grey">mdi-sim</v-icon>
                                <span class="caption">{{ tx.means_of_payment }}</span>
                              </div>
                              <div v-if="tx.transactor_number && tx.transactor_number !== '-'" class="caption text--secondary">
                                <v-icon x-small>mdi-phone</v-icon> {{ tx.transactor_number }}
                              </div>
                              <div v-if="tx.top_up_admin && tx.top_up_admin !== '-' && tx.source !== 'campaign_transaction'" class="caption text--secondary">
                                <v-icon x-small>mdi-account</v-icon> {{ tx.top_up_admin }}
                              </div>
                              <div v-if="tx.fee > 0" class="caption" style="color:#E53935">Fee: −{{ tx.fee }} F CFA</div>
                              <div v-if="tx.wallet_description && tx.wallet_description !== '-'" class="caption text--secondary text-truncate" style="max-width:140px">{{ tx.wallet_description }}</div>
                              <span v-if="!tx.means_of_payment && !tx.transactor_number && !tx.top_up_admin && !tx.fee && !tx.wallet_description" class="text--secondary">—</span>
                            </td>

                            <td>
                              <span v-if="tx.date_time" class="caption text--secondary">{{ tx.date_time }}</span>
                              <span v-else class="text--secondary">—</span>
                            </td>

                            <td class="text-right">
                              <span v-if="tx.pending" class="caption text--secondary font-italic">(pending)</span>
                              <span v-else-if="tx.amount" class="font-weight-bold" :style="{ color: tx.withdrawal ? '#FB8C00' : '#00897B' }">
                                {{ tx.withdrawal ? '−' : '+' }}{{ tx.amount }} F CFA
                              </span>
                              <span v-else class="text--secondary">—</span>
                            </td>

                            <td class="text-right">
                              <span v-if="tx.pending" class="caption text--secondary font-italic">(not counted)</span>
                              <span v-else class="font-weight-bold">{{ tx.running_total }} F CFA</span>
                            </td>
                          </tr>

                          <tr v-if="!principalCampaign.transactions || principalCampaign.transactions.length === 0">
                            <td colspan="7" class="text-center text--secondary py-6">
                              <v-icon large color="grey lighten-2">mdi-inbox-outline</v-icon>
                              <div>No transactions found.</div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-tab-item>

                  <v-tab-item>
                    <div v-if="principalCampaign.completed_contributions && principalCampaign.completed_contributions.length > 0">
                      <v-list dense>
                        <v-list-item v-for="camp in principalCampaign.completed_contributions" :key="camp.id" class="mb-1">
                          <v-list-item-icon><v-icon color="green">mdi-check-circle</v-icon></v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>
                              <router-link :to="'/savings/clients/campaign/'+camp.id" @click.native="dialogPrincipal = false">
                                <span class="font-weight-semibold primary--text">{{ camp.name || '—' }}</span>
                              </router-link>
                            </v-list-item-title>
                            <v-list-item-subtitle class="caption">
                              {{ camp.date || '—' }} ·
                              <v-chip v-if="camp.status" x-small dark :color="statusColor[camp.status] || 'grey'">{{ camp.status }}</v-chip>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <div class="text-right">
                              <div v-if="camp.saved_amount" class="font-weight-bold" style="color:#00897B">+{{ camp.saved_amount }} F CFA</div>
                              <div v-else class="text--secondary">—</div>
                              <div class="caption text--secondary">→ transferred to principal</div>
                            </div>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                      <v-divider class="my-3"></v-divider>
                      <div class="text-right pr-4 pb-3">
                        <span class="caption text--secondary">Total transferred: </span>
                        <span class="font-weight-bold" style="color:#00897B">
                          +{{ principalCampaign.completed_contributions.reduce((s, c) => s + (c.saved_amount || 0), 0) }} F CFA
                        </span>
                      </div>
                    </div>
                    <div v-else class="text-center py-6 text--secondary">
                      <v-icon large color="grey lighten-2">mdi-folder-open-outline</v-icon>
                      <div>No completed campaigns found.</div>
                    </div>
                  </v-tab-item>
                </v-tabs-items>
              </v-card-text>

              <v-card-actions class="py-3">
                <v-spacer></v-spacer>
                <v-btn text color="deep-purple" @click="dialogPrincipal = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

        </v-card>
      </v-tab-item>

    </v-tabs-items>
  </v-card>
</template>

<script>
import { mdiAccountOutline, mdiLockOpenOutline, mdiInformationOutline, mdiBriefcaseOutline } from '@mdi/js'
import { ref } from '@vue/composition-api'
import AccountSettingsAccount from './AccountSettingsAccount.vue'
import AccountSettingsSecurity from './AccountSettingsSecurity.vue'
import AccountSettingsInfo from './AccountSettingsInfo.vue'
import axios from 'axios'

export default {
  components: {
    AccountSettingsAccount,
    AccountSettingsSecurity,
    AccountSettingsInfo,
  },

  setup() {
    const tab = ref('')
    const tabs = [
      { title: 'Info',     icon: mdiInformationOutline },
      { title: 'Account',  icon: mdiAccountOutline },
      { title: 'Security', icon: mdiLockOpenOutline },
      { title: 'Activity', icon: mdiBriefcaseOutline },
    ]
    return { tab, tabs, icons: { mdiAccountOutline, mdiLockOpenOutline, mdiInformationOutline, mdiBriefcaseOutline } }
  },

  data() {
    return {
      statusColor: {
        Ongoing: 'primary', Completed: 'success',
        Interrupted: 'error', Inactivated: 'error',
      },
      headers: [
        { text: 'Name',         value: 'name' },
        { text: 'Amount Saved', value: 'saved_amount' },
        { text: 'Status',       value: 'status' },
        { text: 'Date/Time',    value: 'get_date_str' },
      ],
      walletHeaders: [
        { text: 'Type',        value: 'transaction_type' },
        { text: 'Amount',      value: 'amount' },
        { text: 'Status',      value: 'status' },
        { text: 'Description', value: 'description' },
        { text: 'Date/Time',   value: 'created_at' },
      ],
      // ✅ Colonnes alignées avec les vrais champs API
      saveTransactionHeaders: [
        { text: 'Type',      value: 'withdrawal' },
        { text: 'Campaign',  value: 'campaign' },
        { text: 'Operator',  value: 'transaction_operator' },
        { text: 'Amount',    value: 'amount' },
        { text: 'Phone',     value: 'phone' },
        { text: 'Status',    value: 'confirmed' },
        { text: 'Date/Time', value: 'time_created' },
      ],
      client_id: this.$route.params.client_id,
      loading: false,
      searchCampaigns: '',
      searchLoans: '',
      searchSaveTransactions: '',
      savings_total: '',
      owing_total: '',
      clientCampaigns: [],
      clientLoans: [],
      walletBalance: '0',
      walletTransactions: [],
      saveTransactions: [],
      principalCampaign: null,
      dialogPrincipal: false,
      principalTab: 0,
    }
  },

  computed: {
    filteredSaveTransactions() {
      return (this.saveTransactions || []).filter(tx => tx.amount && Number(tx.amount) !== 0)
    },

    principalTransactionsWithRunning() {
      if (!this.principalCampaign?.transactions) return []
      let running = 0
      return this.principalCampaign.transactions
        .filter(tx => tx.amount && Number(tx.amount) !== 0)
        .map(tx => {
          if (!tx.pending) {
            running = tx.withdrawal
              ? running - (Number(tx.amount) || 0)
              : running + (Number(tx.amount) || 0)
          }
          return { ...tx, running_total: running }
        })
    },

    principalSummary() {
      if (!this.principalCampaign?.transactions) {
        return { totalDeposits: 0, totalWithdrawals: 0, balance: 0, totalPending: 0, pendingCount: 0 }
      }
      let totalDeposits = 0, totalWithdrawals = 0, totalPending = 0, pendingCount = 0
      for (const tx of this.principalCampaign.transactions) {
        if (tx.pending) {
          totalPending += Number(tx.amount) || 0
          pendingCount++
        } else if (tx.withdrawal) {
          totalWithdrawals += Number(tx.amount) || 0
        } else {
          totalDeposits += Number(tx.amount) || 0
        }
      }
      return {
        totalDeposits,
        totalWithdrawals,
        balance: totalDeposits - totalWithdrawals,
        totalPending,
        pendingCount,
      }
    },
  },

  methods: {
    walletTypeHexColor(type) {
      return { DEPOSIT: '#00897B', WITHDRAWAL: '#FB8C00', TRANSFER_IN: '#00897B', TRANSFER_OUT: '#E53935' }[type] || '#757575'
    },
    walletTypeIcon(type) {
      return { DEPOSIT: 'mdi-arrow-down-bold-circle', WITHDRAWAL: 'mdi-arrow-up-bold-circle', TRANSFER_IN: 'mdi-account-arrow-left', TRANSFER_OUT: 'mdi-account-arrow-right' }[type] || 'mdi-swap-horizontal'
    },
    walletTypeLabel(type) {
      return { DEPOSIT: 'Deposit to Wallet', WITHDRAWAL: 'Withdrawal from Wallet', TRANSFER_IN: 'Transfer In', TRANSFER_OUT: 'Transfer Out' }[type] || (type || '—')
    },
    walletStatusHexColor(status) {
      return { PENDING: '#FFB300', COMPLETED: '#00897B', FAILED: '#E53935' }[status] || '#757575'
    },
    sourceHexColor(source) {
      return { completed_campaign: '#43A047', wallet_transaction: '#3949AB', request_save: '#1E88E5', campaign_transaction: '#546E7A' }[source] || '#757575'
    },
    sourceChipIcon(source) {
      return { completed_campaign: 'mdi-check-circle', wallet_transaction: 'mdi-wallet', request_save: 'mdi-cellphone', campaign_transaction: 'mdi-bank-transfer' }[source] || 'mdi-help-circle'
    },
    sourceChipLabel(source) {
      return { completed_campaign: 'Completed Campaign', wallet_transaction: 'Wallet', request_save: 'Mobile Request', campaign_transaction: 'Admin Tx' }[source] || (source || '—')
    },
    async getClientCampaigns() {
      this.loading = true
      await axios.get('/api/v1/manager/get/client/campaigns/' + this.client_id + '/')
        .then(r => { this.clientCampaigns = r.data['user_campaigns'] || []; this.savings_total = r.data['total_saved'] || 0 })
        .catch(() => this.showError("An error occurred while loading campaigns"))
      this.loading = false
    },
    async getClientLoans() {
      this.loading = true
      await axios.get('/api/v1/manager/get/client/loans/' + this.client_id + '/')
        .then(r => { this.clientLoans = r.data['user_loans'] || []; this.owing_total = r.data['total_owing'] || 0 })
        .catch(() => this.showError("An error occurred while loading loans"))
      this.loading = false
    },
    async getClientWallet() {
      this.loading = true
      await axios.get('/api/v1/manager/get/client/wallet/' + this.client_id + '/')
        .then(r => {
          this.walletBalance = r.data['balance'] || 0
          this.walletTransactions = (r.data['transactions'] || []).map(tx => ({
            ...tx,
            transaction_type_display: this.walletTypeLabel(tx.transaction_type),
            source_display: this.formatSourceDest(tx.source),
            destination_display: this.formatSourceDest(tx.destination),
            is_credit: ['DEPOSIT', 'TRANSFER_IN'].includes(tx.transaction_type),
          }))
          this.saveTransactions  = r.data['save_transactions']  || []
          this.principalCampaign = r.data['principal_campaign'] || null
        })
        .catch(() => this.showError("An error occurred while loading wallet"))
      this.loading = false
    },
    formatSourceDest(value) {
      return { BUSINESS_BALANCE: 'Business Balance', MARKETER_CAMPAIGN: 'Marketer Campaign', PRINCIPAL_CAMPAIGN: 'Principal Campaign', WALLET: 'Wallet' }[value] || (value || '—')
    },
    formatDate(dateString) {
      if (!dateString) return '—'
      const d = new Date(dateString)
      if (isNaN(d)) return dateString
      return d.toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    showError(msg) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },
  },

  mounted() {
    document.title = "PettyCash: Manager | Client's Profile"
    this.getClientWallet()
    this.getClientCampaigns()
    this.getClientLoans()
  },
}
</script>
